import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { addToQueue } from '../ark/utils/queueStorage';
import './jobs.css';

const ROLE_CATEGORIES = [
  'Software Engineering',
  'Data & Analytics',
  'Product',
  'Sales',
  'Marketing',
  'Operations',
  'Finance & Accounting',
  'Legal',
  'HR & Recruiting',
  'Design',
];

const ROLE_KEYWORDS = {
  'Software Engineering': ['engineer', 'developer', 'swe', 'backend', 'frontend', 'fullstack', 'full-stack', 'infrastructure', 'devops', 'platform', 'mobile', 'ios', 'android', 'firmware', 'embedded', 'software'],
  'Data & Analytics': ['data', 'analyst', 'analytics', 'scientist', 'ml', 'machine learning', 'ai ', 'research', 'quantitative', 'quant', 'intelligence', 'insights', 'bi '],
  'Product': ['product manager', 'product lead', 'pm ', 'product owner', 'product design', 'ux', 'ui ', 'user experience', 'product strategy'],
  'Sales': ['sales', 'account executive', 'ae ', 'business development', 'bdr', 'sdr', 'revenue', 'partnerships', 'account manager', 'customer success', 'client'],
  'Marketing': ['marketing', 'growth', 'brand', 'content', 'social media', 'seo', 'demand gen', 'communications', 'pr ', 'public relations', 'copywriter'],
  'Operations': ['operations', 'ops ', 'program manager', 'project manager', 'strategy', 'chief of staff', 'supply chain', 'logistics', 'process'],
  'Finance & Accounting': ['finance', 'financial', 'accounting', 'accountant', 'controller', 'cfo', 'treasury', 'tax', 'audit', 'fp&a', 'investment'],
  'Legal': ['legal', 'counsel', 'attorney', 'compliance', 'privacy', 'regulatory', 'policy'],
  'HR & Recruiting': ['recruiter', 'recruiting', 'talent', 'people ops', 'hr ', 'human resources', 'compensation', 'benefits', 'learning'],
  'Design': ['design', 'designer', 'creative', 'visual', 'motion', 'illustrat'],
};

const WORK_MODE_LABELS = { remote: 'Remote', hybrid: 'Hybrid', onsite: 'On-site', unspecified: 'On-site' };

function getDept(jobTitle) {
  const t = jobTitle.toLowerCase();
  for (const [dept, kws] of Object.entries(ROLE_KEYWORDS))
    if (kws.some(kw => t.includes(kw))) return dept;
  return 'Other';
}

function inferWorkMode(job) {
  if (job.work_mode && job.work_mode !== 'unspecified') return job.work_mode;
  const t = job.job_title.toLowerCase();
  if (/\bremote\b/.test(t)) return 'remote';
  if (/\bhybrid\b/.test(t)) return 'hybrid';
  if (/\bon-?site\b/.test(t)) return 'onsite';
  return 'unspecified';
}

function cleanTitle(title) {
  let t = title.split(/[•·]/)[0].trim();
  t = t.replace(/\s+APPLY\b.*/i, '').trim();
  t = t.replace(/\s+(Remote|Hybrid|On-?site)(\s*[-–,/]\s*.+)?$/i, '').trim();
  return t;
}

function matchesRole(jobTitle, roleCategory) {
  const title = jobTitle.toLowerCase();
  const keywords = ROLE_KEYWORDS[roleCategory] || [];
  return keywords.some(kw => title.includes(kw));
}

function toggleItem(arr, item) {
  return arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
}

const DATE_POSTED_OPTIONS = {
  '': 'Any time',
  '1': 'Last 24 hours',
  '7': 'Last 7 days',
  '14': 'Last 14 days',
  '30': 'Last 30 days',
};

function trackJobClick(company, industry) {
  try {
    const key = 'ferl_job_prefs';
    const prefs = JSON.parse(localStorage.getItem(key) || '{}');
    if (company) prefs[`co:${company}`] = (prefs[`co:${company}`] || 0) + 1;
    if (industry) prefs[`ind:${industry}`] = (prefs[`ind:${industry}`] || 0) + 1;
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch (_) {}
}

function freshnessLabel(scrapedAt) {
  if (!scrapedAt) return null;
  const days = Math.floor((Date.now() - new Date(scrapedAt).getTime()) / 86400000);
  if (days === 0) return { text: 'Today', cls: 'fresh' };
  if (days === 1) return { text: '1d ago', cls: 'fresh' };
  if (days <= 7) return { text: `${days}d ago`, cls: 'fresh' };
  if (days <= 30) return { text: `${days}d ago`, cls: 'aging' };
  return { text: `${days}d ago`, cls: 'stale' };
}

function FilterBtn({ label, count, isOpen, onToggle, children, hasSelection }) {
  return (
    <div className="jobs-filter-group">
      <button
        className={`jobs-filter-btn ${hasSelection ? 'has-selection' : ''}`}
        onClick={onToggle}
      >
        {label}
        {count > 0 && <span className="jobs-filter-count">{count}</span>}
        <span className="jobs-filter-caret">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && <div className="jobs-dropdown">{children}</div>}
    </div>
  );
}

function jobKey(job) {
  return job.job_url || `${job.company_name}__${job.job_title}`;
}

export default function OpenRolesPage({ selectedIndustries, setSelectedIndustries, selectedRoles, setSelectedRoles }) {
  // Company summaries (server-sorted: thesis first, then by job count)
  const [companies, setCompanies] = useState([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [totalJobsFromServer, setTotalJobsFromServer] = useState(0);
  const [allIndustries, setAllIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lazy-loaded jobs per company — keyed by company_id (string)
  const [companyJobs, setCompanyJobs] = useState({});
  const [loadingJobs, setLoadingJobs] = useState(new Set());

  // Expand state
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [expandedDepts, setExpandedDepts] = useState(new Set()); // "companyId::dept"

  // Filters
  const [search, setSearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedWorkModes, setSelectedWorkModes] = useState([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [datePosted, setDatePosted] = useState('');

  // Selection
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [openDropdown, setOpenDropdown] = useState(null);

  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Pagination
  const PAGE_SIZE = 250;
  const [currentPage, setCurrentPage] = useState(0);

  // Refs to avoid stale closures in async fetches
  const companyJobsCacheRef = useRef({});
  const loadingJobsRef = useRef(new Set());

  // All loaded jobs across all expanded companies (for select-all, ark)
  const allLoadedJobs = Object.values(companyJobs).flat();
  const allJobKeys = allLoadedJobs.map(jobKey);
  const allSelected = allJobKeys.length > 0 && allJobKeys.every(k => selectedJobs.has(k));
  const someSelected = !allSelected && allJobKeys.some(k => selectedJobs.has(k));

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedCompanies.length) params.set('company', selectedCompanies[0]);
    if (selectedIndustries.length) params.set('industry', selectedIndustries.join(','));
    if (selectedWorkModes.length) params.set('work_mode', selectedWorkModes.join(','));
    if (locationSearch) params.set('location', locationSearch);
    if (datePosted) params.set('posted_days', datePosted);
    return params.toString();
  }, [search, selectedCompanies, selectedIndustries, selectedWorkModes, locationSearch, datePosted]);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    companyJobsCacheRef.current = {};
    loadingJobsRef.current = new Set();
    setCompanyJobs({});
    setLoadingJobs(new Set());
    setExpandedCompanies(new Set());
    setExpandedDepts(new Set());
    setSelectedJobs(new Set());

    try {
      const params = new URLSearchParams(buildFilterParams());
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(currentPage * PAGE_SIZE));
      const res = await fetch(`/api/jobs/companies?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch companies');
      const data = await res.json();
      setCompanies(data.companies || []);
      setTotalCompanies(data.totalCompanies || 0);
      setTotalJobsFromServer(data.totalJobs || 0);
      setAllIndustries(data.industries || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, currentPage]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Reset to page 0 when any filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [search, selectedCompanies, selectedIndustries, selectedWorkModes, locationSearch, datePosted]);

  const doFetchCompanyJobs = useCallback(async (companyId, filterStr) => {
    const key = String(companyId);
    if (companyJobsCacheRef.current[key] !== undefined) return;
    if (loadingJobsRef.current.has(key)) return;

    loadingJobsRef.current.add(key);
    setLoadingJobs(new Set(loadingJobsRef.current));

    try {
      const res = await fetch(`/api/jobs/company/${companyId}/jobs?${filterStr}`);
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const jobs = data.jobs || [];

      companyJobsCacheRef.current[key] = jobs;
      setCompanyJobs(prev => ({ ...prev, [key]: jobs }));


    } catch (_) {
      companyJobsCacheRef.current[key] = [];
      setCompanyJobs(prev => ({ ...prev, [key]: [] }));
    } finally {
      loadingJobsRef.current.delete(key);
      setLoadingJobs(new Set(loadingJobsRef.current));
    }
  }, []);

  const toggleCompany = useCallback((companyId) => {
    const key = String(companyId);
    const isCurrentlyExpanded = expandedCompanies.has(key);
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      isCurrentlyExpanded ? next.delete(key) : next.add(key);
      return next;
    });
    if (!isCurrentlyExpanded) {
      doFetchCompanyJobs(companyId, buildFilterParams());
    }
  }, [expandedCompanies, doFetchCompanyJobs, buildFilterParams]);

  const toggleDept = useCallback((companyId, dept) => {
    const key = `${companyId}::${dept}`;
    setExpandedDepts(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const toggleJob = (key) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(allJobKeys));
    }
  };

  const clearSelection = () => setSelectedJobs(new Set());

  const handleSendToArk = () => {
    const selected = allLoadedJobs.filter(j => selectedJobs.has(jobKey(j)));
    const payload = selected.map(j => ({
      _key: jobKey(j),
      job_title: j.job_title,
      company_name: j.company_name,
      job_url: j.job_url || null,
      work_mode: j.work_mode,
      industry: j.industry,
    }));
    addToQueue(payload);
    clearSelection();
    navigate('/jobs/ark');
  };

  const clearAll = () => {
    setSearch('');
    setSelectedIndustries([]);
    setSelectedRoles([]);
    setSelectedCompanies([]);
    setSelectedWorkModes([]);
    setLocationSearch('');
    setDatePosted('');
    if (searchRef.current) searchRef.current.value = '';
  };

  const activeCount =
    selectedIndustries.length + selectedRoles.length +
    selectedCompanies.length + selectedWorkModes.length +
    (search ? 1 : 0) + (locationSearch ? 1 : 0) + (datePosted ? 1 : 0);

  const totalJobs = totalJobsFromServer || companies.reduce((sum, c) => sum + (c.active_job_count || 0), 0);

  // Company names for dropdown — derived from loaded company summaries
  const allCompanyNames = companies.map(c => c.company_name);

  const closeDropdowns = () => setOpenDropdown(null);

  return (
    <div className="jobs-panel">
      <header className="jobs-page-topbar">
        <span className="jobs-page-title">Open Roles</span>
      </header>

      <div className="jobs-page">
        {/* ── Filter bar ── */}
        <div className="jobs-filters">
          <div className="or-search-wrap">
            <input
              ref={searchRef}
              type="text"
              className="or-search"
              placeholder="Search job title or company..."
              defaultValue={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <FilterBtn
            label="Industry"
            count={selectedIndustries.length}
            isOpen={openDropdown === 'industry'}
            onToggle={() => setOpenDropdown(o => o === 'industry' ? null : 'industry')}
            hasSelection={selectedIndustries.length > 0}
          >
            {allIndustries.map(ind => (
              <label key={ind} className="jobs-dropdown-item">
                <input
                  type="checkbox"
                  checked={selectedIndustries.includes(ind)}
                  onChange={() => setSelectedIndustries(prev => toggleItem(prev, ind))}
                />
                {ind}
              </label>
            ))}
          </FilterBtn>

          <FilterBtn
            label="Role"
            count={selectedRoles.length}
            isOpen={openDropdown === 'role'}
            onToggle={() => setOpenDropdown(o => o === 'role' ? null : 'role')}
            hasSelection={selectedRoles.length > 0}
          >
            {ROLE_CATEGORIES.map(role => (
              <label key={role} className="jobs-dropdown-item">
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(role)}
                  onChange={() => setSelectedRoles(prev => toggleItem(prev, role))}
                />
                {role}
              </label>
            ))}
          </FilterBtn>

          <FilterBtn
            label="Company"
            count={selectedCompanies.length}
            isOpen={openDropdown === 'company'}
            onToggle={() => setOpenDropdown(o => o === 'company' ? null : 'company')}
            hasSelection={selectedCompanies.length > 0}
          >
            <div className="or-company-search-wrap">
              <input
                type="text"
                className="or-dropdown-search"
                placeholder="Filter companies..."
                onClick={e => e.stopPropagation()}
                onChange={e => {
                  const q = e.target.value.toLowerCase();
                  const items = document.querySelectorAll('.or-company-item');
                  items.forEach(el => {
                    el.style.display = el.dataset.name.includes(q) ? '' : 'none';
                  });
                }}
              />
            </div>
            {allCompanyNames.map(co => (
              <label key={co} className="jobs-dropdown-item or-company-item" data-name={co.toLowerCase()}>
                <input
                  type="checkbox"
                  checked={selectedCompanies.includes(co)}
                  onChange={() => setSelectedCompanies(prev => toggleItem(prev, co))}
                />
                {co}
              </label>
            ))}
          </FilterBtn>

          <FilterBtn
            label="Work Mode"
            count={selectedWorkModes.length}
            isOpen={openDropdown === 'workmode'}
            onToggle={() => setOpenDropdown(o => o === 'workmode' ? null : 'workmode')}
            hasSelection={selectedWorkModes.length > 0}
          >
            {Object.entries(WORK_MODE_LABELS).map(([val, label]) => (
              <label key={val} className="jobs-dropdown-item">
                <input
                  type="checkbox"
                  checked={selectedWorkModes.includes(val)}
                  onChange={() => setSelectedWorkModes(prev => toggleItem(prev, val))}
                />
                {label}
              </label>
            ))}
          </FilterBtn>

          <div className="or-location-wrap">
            <input
              type="text"
              className="or-location-input"
              placeholder="Location..."
              value={locationSearch}
              onChange={e => setLocationSearch(e.target.value)}
            />
          </div>

          <FilterBtn
            label="Posted"
            count={datePosted ? 1 : 0}
            isOpen={openDropdown === 'dateposted'}
            onToggle={() => setOpenDropdown(o => o === 'dateposted' ? null : 'dateposted')}
            hasSelection={!!datePosted}
          >
            {Object.entries(DATE_POSTED_OPTIONS).map(([val, label]) => (
              <label key={val || 'any'} className="jobs-dropdown-item">
                <input
                  type="radio"
                  name="datePosted"
                  checked={datePosted === val}
                  onChange={() => { setDatePosted(val); setOpenDropdown(null); }}
                />
                {label}
              </label>
            ))}
          </FilterBtn>

          {activeCount > 0 && (
            <div className="jobs-chips">
              {search && (
                <span className="jobs-chip role">
                  "{search}"
                  <button onClick={() => { setSearch(''); if (searchRef.current) searchRef.current.value = ''; }}>×</button>
                </span>
              )}
              {locationSearch && (
                <span className="jobs-chip role">
                  📍 {locationSearch}
                  <button onClick={() => setLocationSearch('')}>×</button>
                </span>
              )}
              {selectedIndustries.map(ind => (
                <span key={ind} className="jobs-chip industry">
                  {ind}
                  <button onClick={() => setSelectedIndustries(p => p.filter(x => x !== ind))}>×</button>
                </span>
              ))}
              {selectedRoles.map(r => (
                <span key={r} className="jobs-chip role">
                  {r}
                  <button onClick={() => setSelectedRoles(p => p.filter(x => x !== r))}>×</button>
                </span>
              ))}
              {selectedCompanies.map(co => (
                <span key={co} className="jobs-chip company">
                  {co}
                  <button onClick={() => setSelectedCompanies(p => p.filter(x => x !== co))}>×</button>
                </span>
              ))}
              {selectedWorkModes.map(m => (
                <span key={m} className="jobs-chip workmode">
                  {WORK_MODE_LABELS[m]}
                  <button onClick={() => setSelectedWorkModes(p => p.filter(x => x !== m))}>×</button>
                </span>
              ))}
              {datePosted && (
                <span className="jobs-chip industry">
                  {DATE_POSTED_OPTIONS[datePosted]}
                  <button onClick={() => setDatePosted('')}>×</button>
                </span>
              )}
              <button className="jobs-clear-btn" onClick={clearAll}>Clear all</button>
            </div>
          )}
        </div>

        {openDropdown && <div className="jobs-overlay" onClick={closeDropdowns} />}

        {/* ── Results ── */}
        <main className="jobs-content">

          {/* Gmail-style toolbar */}
          <div className="or-toolbar">
            <div className="or-toolbar-left">
              <label className="or-select-all-wrap" title={allSelected ? 'Deselect all' : 'Select all'}>
                <input
                  type="checkbox"
                  className="or-select-all-box"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleSelectAll}
                />
                <span className="or-select-all-caret" onClick={e => { e.preventDefault(); toggleSelectAll(); }}>▾</span>
              </label>
            </div>
            <div className="jobs-count" style={{ margin: 0 }}>
              {loading
                ? 'Loading...'
                : `${totalJobs.toLocaleString()} roles${activeCount > 0 ? ' matching filters' : ''} · ${totalCompanies.toLocaleString()} companies`
              }
            </div>
          </div>

          {error && <div className="jobs-empty">Error loading jobs: {error}</div>}

          {!loading && companies.length === 0 && !error && (
            <div className="jobs-empty">No companies match the selected filters.</div>
          )}

          {/* ── Company list ── */}
          <div className="or-companies">
            {companies.map(company => {
              const cid = String(company.company_id);
              const isExpanded = expandedCompanies.has(cid);
              const isLoadingJobs = loadingJobs.has(cid);
              const jobs = companyJobs[cid] || [];

              // Apply role filter to loaded jobs (client-side, same as before)
              const filteredJobs = selectedRoles.length > 0
                ? jobs.filter(j => selectedRoles.some(r => matchesRole(j.job_title, r)))
                : jobs;

              // Group filtered jobs by department, sorted by dept size desc
              const deptMap = {};
              filteredJobs.forEach(job => {
                const dept = getDept(job.job_title);
                if (!deptMap[dept]) deptMap[dept] = [];
                deptMap[dept].push(job);
              });
              const depts = Object.entries(deptMap).sort((a, b) => b[1].length - a[1].length);

              return (
                <div key={cid} className="or-company-block">
                  {/* Company header — click anywhere to toggle */}
                  <div
                    className="or-company-header or-company-header-clickable"
                    onClick={() => toggleCompany(company.company_id)}
                  >
                    <div className="or-company-header-left">
                      <span className={`or-company-toggle${isExpanded ? ' expanded' : ''}`}>▶</span>
                      <span className="or-company-name">{company.company_name}</span>
                      <span className="or-company-count">
                        {company.active_job_count} role{company.active_job_count !== 1 ? 's' : ''}
                      </span>
                      {company.industry && (
                        <span className="jobs-tag industry">{company.industry}</span>
                      )}
                    </div>
                    {company.careers_url && (
                      <a
                        href={company.careers_url}
                        className="jobs-careers-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                      >
                        Careers page →
                      </a>
                    )}
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="or-company-expanded">
                      {isLoadingJobs ? (
                        <div className="or-dept-loading">Loading roles...</div>
                      ) : depts.length === 0 ? (
                        <div className="or-dept-loading">
                          {selectedRoles.length > 0 ? 'No roles match this function filter.' : 'No active roles found.'}
                        </div>
                      ) : (
                        depts.map(([dept, deptJobs]) => {
                          const deptKey = `${cid}::${dept}`;
                          const isDeptExpanded = expandedDepts.has(deptKey);
                          return (
                            <div key={dept} className="or-dept-block">
                              <div
                                className="or-dept-header"
                                onClick={() => toggleDept(cid, dept)}
                              >
                                <span className={`or-dept-toggle${isDeptExpanded ? ' expanded' : ''}`}>▶</span>
                                <span className="or-dept-name">{dept}</span>
                                <span className="or-dept-count">({deptJobs.length})</span>
                              </div>
                              {isDeptExpanded && (
                                <ul className="or-jobs-list or-dept-jobs">
                                  {deptJobs.map((job, i) => {
                                    const key = jobKey(job);
                                    const isChecked = selectedJobs.has(key);
                                    return (
                                      <li key={i} className={`or-job-row${isChecked ? ' selected' : ''}`}>
                                        <label className="or-job-checkbox-wrap" onClick={e => e.stopPropagation()}>
                                          <input
                                            type="checkbox"
                                            className="or-job-checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleJob(key)}
                                          />
                                        </label>
                                        <div className="or-job-left">
                                          {job.verification_status === 'expired' ? (
                                            <span className="or-job-title expired" title={job.job_description ? 'This job may no longer be available — click to see cached description' : 'This job may no longer be available'}>
                                              {cleanTitle(job.job_title)}
                                              <span className="or-expired-badge">expired</span>
                                            </span>
                                          ) : job.job_url ? (
                                            <a
                                              href={job.job_url}
                                              className="or-job-title"
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={() => trackJobClick(job.company_name, job.industry)}
                                            >
                                              {cleanTitle(job.job_title)}
                                            </a>
                                          ) : (
                                            <span className="or-job-title no-link">{cleanTitle(job.job_title)}</span>
                                          )}
                                          {job.location && (
                                            <span className="or-job-location">{job.location}</span>
                                          )}
                                        </div>
                                        <div className="or-job-right">
                                          {(() => { const f = freshnessLabel(job.scraped_at); return f ? (
                                            <span className={`or-freshness ${f.cls}`}>{f.text}</span>
                                          ) : null; })()}
                                          {(() => { const wm = inferWorkMode(job); return (
                                            <span className={`or-work-mode ${wm}`}>
                                              {WORK_MODE_LABELS[wm]}
                                            </span>
                                          ); })()}
                                          {job.verification_status === 'expired' ? (
                                            job.careers_url && (
                                              <a href={job.careers_url} className="or-apply-link expired" target="_blank" rel="noopener noreferrer">
                                                Careers page →
                                              </a>
                                            )
                                          ) : job.job_url && (
                                            <a href={job.job_url} className="or-apply-link" target="_blank" rel="noopener noreferrer">
                                              Apply →
                                            </a>
                                          )}
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalCompanies > PAGE_SIZE && (
            <div className="or-pagination">
              <button
                className="or-pagination-btn"
                onClick={() => { setCurrentPage(p => Math.max(0, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={currentPage === 0}
              >← Prev</button>
              <span className="or-pagination-label">
                Page {currentPage + 1} of {Math.ceil(totalCompanies / PAGE_SIZE)}
              </span>
              <button
                className="or-pagination-btn"
                onClick={() => { setCurrentPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={(currentPage + 1) * PAGE_SIZE >= totalCompanies}
              >Next →</button>
            </div>
          )}
        </main>

        {/* Floating selection bar */}
        {selectedJobs.size > 0 && (
          <div className="or-selection-bar">
            <span className="or-selection-count">
              {selectedJobs.size} role{selectedJobs.size !== 1 ? 's' : ''} selected
            </span>
            <button className="or-selection-ark-btn" onClick={handleSendToArk}>
              Send to ark →
            </button>
            <button className="or-selection-clear" onClick={clearSelection}>
              Clear
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
